import i18n from '../i18n'
import { faDigits } from './numerals'

/**
 * The progress tab's rules (design page 9, `progress`; P-progress of the
 * go-live plan), mirrored from the Android app's `ProgressScreen.kt` and
 * `WeeklyChart` so both clients read the same seven numbers the same way.
 * Everything comes from `me/stats`: `weekMinutes` is Saturday-first, the
 * order the Iranian week begins and the order the chart draws — that
 * ordering is a business rule, not a Persian-only display choice, so it
 * holds in the English UI too; only the day labels translate.
 */

/** Functions, not constants: the active language decides which array comes back. */
export const weekdayInitials = () => i18n.t('weekdayInitials', { ns: 'progress', returnObjects: true })
export const weekdayNames = () => i18n.t('weekdayNames', { ns: 'progress', returnObjects: true })

/** The chart's geometry, the design's: a peak bar is FLOOR + SPAN tall; an empty day is a 6px stub. */
export const CHART_BAR_FLOOR = 18
export const CHART_BAR_SPAN = 74
export const CHART_EMPTY_BAR = 6

/** Seven non-negative whole numbers, whatever the server sent — a short or missing week reads as empty. */
export function normalizeWeek(minutesByDay) {
  return Array.from({ length: 7 }, (_, i) => Math.max(0, Math.round(Number(minutesByDay?.[i]) || 0)))
}

/**
 * What a week's minutes say. `bestDayIndex` is null for an empty week
 * rather than 0: index 0 is Saturday, a real day, and «بهترین روزت شنبه با
 * صفر دقیقه بود» is worse than saying nothing. Ties take the earliest day,
 * so the report never flickers between two equal days.
 */
export function weekSummary(minutesByDay) {
  const week = normalizeWeek(minutesByDay)
  let bestIndex = 0
  for (let i = 1; i < week.length; i++) if (week[i] > week[bestIndex]) bestIndex = i
  const best = week[bestIndex]
  return {
    totalMinutes: week.reduce((sum, minutes) => sum + minutes, 0),
    activeDays: week.filter((minutes) => minutes > 0).length,
    bestDayIndex: best > 0 ? bestIndex : null,
    bestDayMinutes: best,
  }
}

/**
 * The seven bars: the peak in the brand colour, other practised days in
 * the tint, an empty day a track-coloured stub. The label above a bar is
 * its minutes, and an em dash for a day with nothing on it — «۰» reads as
 * a recorded zero, the dash as a day that never happened.
 */
export function chartBars(minutesByDay) {
  const week = normalizeWeek(minutesByDay)
  const peak = Math.max(...week)
  const initials = weekdayInitials()
  return week.map((minutes, index) => ({
    day: initials[index],
    minutes,
    label: minutes > 0 ? faDigits(minutes) : '—',
    height: minutes > 0 ? Math.round(CHART_BAR_FLOOR + (minutes / peak) * CHART_BAR_SPAN) : CHART_EMPTY_BAR,
    tone: minutes === 0 ? 'none' : minutes === peak ? 'peak' : 'some',
  }))
}
